import { unwrap } from "@sagot/api-client";
import { CONSOLE_PATHS, PASSWORD_MAX_LENGTH, PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Icon, Skeleton, toast } from "@sagot/ui";
import { useQuery } from "@tanstack/react-query";
import { useState, type SyntheticEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useErrorMessage } from "../lib/api-errors";
import { allRulesPass, passwordRules } from "../lib/password";
import { queryKeys } from "../lib/query-keys";
import { useApi } from "../session/session-provider";
import { AuthLayout } from "./components/auth-layout";
import { useCreateAccountPrompt } from "./components/auth-prompts";
import { FormAlert } from "./components/form-alert";
import { IconBadge } from "./components/icon-badge";
import { PasswordInput } from "./components/password-input";
import { PasswordRulesList } from "./components/password-rules";
import { PasswordStrength } from "./components/password-strength";

const TOKEN_MIN_LENGTH = 16;

function BackToSignIn() {
  const { t } = useTranslation();
  return (
    <p className="a-foot">
      <AppLink href={CONSOLE_PATHS.login} className="a-link">
        {t("auth.common.backToSignIn")}
      </AppLink>
    </p>
  );
}

/** Designed state for a reset link that is malformed, used, expired or unknown (the API gives no reason, by design). */
function InvalidLink() {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="rp-invalid-title" data-testid="reset-invalid">
      <span className="a-state" data-tone="warn">
        <Icon name="alert-triangle" />
      </span>
      <h1 id="rp-invalid-title" className="a-title">
        {t("auth.reset.invalidTitle")}
      </h1>
      <p className="a-sub">{t("auth.reset.invalidBody")}</p>
      <Button asChild size="lg" className="w-full">
        <AppLink href={CONSOLE_PATHS.forgotPassword}>{t("auth.reset.requestNew")}</AppLink>
      </Button>
      <BackToSignIn />
    </section>
  );
}

function ResetSkeleton() {
  return (
    <div aria-busy="true" data-testid="reset-loading">
      <Skeleton className="mx-auto mb-5 size-[72px] rounded-full" />
      <Skeleton className="mx-auto mb-3 h-10 w-[260px] max-w-full" />
      <Skeleton className="mx-auto mb-7 h-4 w-[300px] max-w-full" />
      <Skeleton className="mb-4 h-[50px] w-full rounded-full" />
      <Skeleton className="mb-4 h-[50px] w-full rounded-full" />
      <Skeleton className="h-[50px] w-full rounded-full" />
    </div>
  );
}

function ResetForm({ token, email }: { token: string; email: string }) {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const message = useErrorMessage();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const rules = passwordRules(password, confirm);
  const ready = allRulesPass(rules) && password.length <= PASSWORD_MAX_LENGTH;

  const onSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!ready) return;
    setSubmitting(true);
    setError(null);
    try {
      await unwrap(api.POST("/v1/auth/reset", { body: { token, password } }));
      toast.success(t("auth.reset.done"));
      await navigate(CONSOLE_PATHS.login, { replace: true });
    } catch (caught) {
      setError(message(caught));
      setSubmitting(false);
    }
  };

  return (
    <section aria-labelledby="rp-title">
      <IconBadge icon="lock" />
      <h1 id="rp-title" className="a-title">
        {t("auth.reset.heading")}
      </h1>
      <p className="a-sub">
        {t("auth.reset.forLead")} <strong>{email}</strong>. {t("auth.reset.forTail")}
      </p>
      {error ? (
        <FormAlert
          title={error}
          onDismiss={() => {
            setError(null);
          }}
        />
      ) : null}
      <form noValidate onSubmit={(event) => void onSubmit(event)}>
        <div className="a-fld">
          <label className="label" htmlFor="rp-pw">
            {t("auth.reset.newPassword")}
          </label>
          {/* eslint-disable-next-line jsx-a11y/no-autofocus -- the new password is the page's only task */}
          <PasswordInput id="rp-pw" placeholder={t("auth.password.placeholder")} autoComplete="new-password" autoFocus value={password} onChange={(e) => { setPassword(e.target.value); }} />
          <PasswordStrength value={password} />
        </div>
        <div className="a-fld">
          <label className="label" htmlFor="rp-confirm">
            {t("auth.reset.confirm")}
          </label>
          <PasswordInput id="rp-confirm" autoComplete="new-password" value={confirm} onChange={(e) => { setConfirm(e.target.value); }} />
        </div>
        <PasswordRulesList rules={rules} />
        <Button type="submit" size="lg" className="w-full" loading={submitting} disabled={submitting || !ready}>
          {t("auth.reset.submit")}
        </Button>
      </form>
      <BackToSignIn />
    </section>
  );
}

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const api = useApi();
  const prompt = useCreateAccountPrompt();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const valid = token.length >= TOKEN_MIN_LENGTH;
  const validation = useQuery({
    queryKey: queryKeys.resetToken(token),
    queryFn: () => unwrap(api.GET("/v1/auth/reset/validate", { params: { query: { token } } })),
    enabled: valid,
    retry: false,
    staleTime: Infinity,
  });

  let content;
  if (!valid || validation.isError) content = <InvalidLink />;
  else if (validation.isPending) content = <ResetSkeleton />;
  else content = <ResetForm token={token} email={validation.data.email} />;

  return (
    <AuthLayout title={t("auth.reset.title", { product: PRODUCT_NAME })} prompt={prompt}>
      {content}
    </AuthLayout>
  );
}
